import { graphql, HttpResponse } from "msw";
import { GraphQL } from "@rpi/openapi-api";

// Note: for mock data, only the RECEIVED status is mapped to inProgress to simplify setup.
// In production, data will be mapped to inProgress for all statuses in that category: RECEIVED, QUEUED, VALIDATING, VALID_AWAITING_PAYMENT, VALID_HOLDING_BIN.
const createStatusCounts = (shipped = 0, printing = 0, inProgress = 0, failed = 0) => [
  { status: GraphQL.PublicOrderStatus.Shipped, count: shipped },
  { status: GraphQL.PublicOrderStatus.Printing, count: printing },
  { status: GraphQL.PublicOrderStatus.Received, count: inProgress },
  { status: GraphQL.PublicOrderStatus.Failed, count: failed },
];

/**
 * @featureFlag useMocks
 * @description when active, use any existing mock service worker handlers instead of real calls
 */
// Set Feature Flag if this is a Vercel preview, and it hasn't been set
if (window.location.hostname.includes("vercel.app") && localStorage.getItem("useMocks") === null) {
  localStorage.setItem("useMocks", "true");
}
export const useMocks = localStorage.getItem("useMocks") === "true";
const MOCK_ORDERS_DATA: Record<number, NonNullable<GraphQL.OrdersChartQuery["ordersChart"]>["dataPoints"]> = {
  2024: [
    { month: "2024-01-01", statusCounts: createStatusCounts(140) },
    { month: "2024-02-01", statusCounts: createStatusCounts(165, 0, 0, 10) },
    { month: "2024-03-01", statusCounts: createStatusCounts(175) },
    { month: "2024-04-01", statusCounts: createStatusCounts(80, 0, 0, 5) },
    { month: "2024-05-01", statusCounts: createStatusCounts(75) },
    { month: "2024-06-01", statusCounts: createStatusCounts(80, 0, 0, 5) },
    { month: "2024-07-01", statusCounts: createStatusCounts(60) },
    { month: "2024-08-01", statusCounts: createStatusCounts(175) },
    { month: "2024-09-01", statusCounts: createStatusCounts(175) },
    { month: "2024-10-01", statusCounts: createStatusCounts(175) },
    { month: "2024-11-01", statusCounts: createStatusCounts(175) },
    { month: "2024-12-01", statusCounts: createStatusCounts(300) },
  ],
  2025: [
    { month: "2025-01-01", statusCounts: createStatusCounts(150) },
    { month: "2025-02-01", statusCounts: createStatusCounts(180, 0, 0, 8) },
    { month: "2025-03-01", statusCounts: createStatusCounts(190) },
    { month: "2025-04-01", statusCounts: createStatusCounts(95, 0, 0, 3) },
    { month: "2025-05-01", statusCounts: createStatusCounts(85) },
    { month: "2025-06-01", statusCounts: createStatusCounts(90, 0, 0, 4) },
    { month: "2025-07-01", statusCounts: createStatusCounts(70, 110, 50, 5) },
    { month: "2025-08-01", statusCounts: createStatusCounts(175) },
    { month: "2025-09-01", statusCounts: createStatusCounts(175) },
    { month: "2025-10-01", statusCounts: createStatusCounts(175) },
    { month: "2025-11-01", statusCounts: createStatusCounts(175) },
    { month: "2025-12-01", statusCounts: createStatusCounts(300) },
  ],
  2026: [
    { month: "2026-01-01", statusCounts: createStatusCounts(170) },
    { month: "2026-02-01", statusCounts: createStatusCounts(0) },
    { month: "2026-03-01", statusCounts: createStatusCounts(0) },
    { month: "2026-04-01", statusCounts: createStatusCounts(0) },
    { month: "2026-05-01", statusCounts: createStatusCounts(0) },
    { month: "2026-06-01", statusCounts: createStatusCounts(0) },
    { month: "2026-07-01", statusCounts: createStatusCounts(0) },
    { month: "2026-08-01", statusCounts: createStatusCounts(0) },
    { month: "2026-09-01", statusCounts: createStatusCounts(0) },
    { month: "2026-10-01", statusCounts: createStatusCounts(0) },
    { month: "2026-11-01", statusCounts: createStatusCounts(0) },
    { month: "2026-12-01", statusCounts: createStatusCounts(0) },
  ],
};

const MOCK_PAYMENTS_DATA: Record<number, NonNullable<GraphQL.PaymentsChartQuery["paymentsChart"]>["dataPoints"]> = {
  2024: [
    { month: "2024-01-01", netPaymentAmount: 1400, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-02-01", netPaymentAmount: 4650, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-03-01", netPaymentAmount: 10000, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-04-01", netPaymentAmount: 8000, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-05-01", netPaymentAmount: 7500, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-06-01", netPaymentAmount: 8000, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-07-01", netPaymentAmount: 6000, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-08-01", netPaymentAmount: 1750, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-09-01", netPaymentAmount: 17500, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-10-01", netPaymentAmount: 1750, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-11-01", netPaymentAmount: 17500, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2024-12-01", netPaymentAmount: 3000, currency: GraphQL.SupportedCurrency.Usd },
  ],
  2025: [
    { month: "2025-01-01", netPaymentAmount: 1500, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-02-01", netPaymentAmount: 1800, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-03-01", netPaymentAmount: 19000, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-04-01", netPaymentAmount: 950, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-05-01", netPaymentAmount: 8500, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-06-01", netPaymentAmount: 9000, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-07-01", netPaymentAmount: 7000, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-08-01", netPaymentAmount: 1750, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-09-01", netPaymentAmount: 17500, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-10-01", netPaymentAmount: 1750, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-11-01", netPaymentAmount: 17500, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2025-12-01", netPaymentAmount: 3000, currency: GraphQL.SupportedCurrency.Usd },
  ],
  2026: [
    { month: "2026-01-01", netPaymentAmount: 7000, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-02-01", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-03-01", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-04-01", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-05-01", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-06-01", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-07-01", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-08-01", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-09-01", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-10-01", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-11-01", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
    { month: "2026-12-01", netPaymentAmount: 0, currency: GraphQL.SupportedCurrency.Usd },
  ],
};

const AVAILABLE_YEARS = [2024, 2025, 2026];

export const handlers = [
  graphql.query("OrdersChart", ({ variables }) => {
    const year = variables.year as number;

    return HttpResponse.json<{ data: GraphQL.OrdersChartQuery }>({
      data: {
        ordersChart: {
          availableYears: AVAILABLE_YEARS,
          dataPoints: MOCK_ORDERS_DATA[year] ?? [],
        },
      },
    });
  }),
  graphql.query("PaymentsChart", ({ variables }) => {
    const year = variables.year as number;

    return HttpResponse.json<{ data: GraphQL.PaymentsChartQuery }>({
      data: {
        paymentsChart: {
          availableYears: AVAILABLE_YEARS,
          nextChargeDate: "2026-07-01",
          dataPoints: MOCK_PAYMENTS_DATA[year] ?? [],
        },
      },
    });
  }),
];
